import type {
  Vessel,
  BillOfLading,
  ShippingQuoteRequest,
  LegalConsultant,
  LegalTemplate,
  LegalService,
  LegalResource,
  SecurityReport,
  SecurityContact,
  DisputeCase,
  MarketplaceListing,
  SellerProfile,
  PurchaseRequest,
  KnowledgeResource,
} from '../types/maritime'

// Vessel Tracking Dummy Data
export const dummyVessels: Vessel[] = [
  {
    id: 'v1',
    imo: '9876543',
    mmsi: '123456789',
    name: 'MSC GENEVA',
    type: 'container',
    flag: 'Panama',
    destination: 'SINGAPORE',
    eta: '2025-01-15T14:00:00Z',
    status: 'underway',
    lat: 1.2655,
    lng: 103.8192,
    speed: 18.5,
    heading: 45,
    course: 47,
    lastUpdate: '2025-01-10T08:30:00Z',
  },
  {
    id: 'v2',
    imo: '9754321',
    mmsi: '987654321',
    name: 'EVER GIVEN',
    type: 'container',
    flag: 'Panama',
    destination: 'ROTTERDAM',
    eta: '2025-01-18T09:00:00Z',
    status: 'underway',
    lat: 31.2857,
    lng: 32.3019,
    speed: 13.2,
    heading: 330,
    course: 332,
    lastUpdate: '2025-01-10T07:45:00Z',
  },
  {
    id: 'v3',
    imo: '9632145',
    mmsi: '456789123',
    name: 'MAERSK EDINBURGH',
    type: 'container',
    flag: 'Denmark',
    destination: 'LAGOS',
    eta: '2025-01-12T16:00:00Z',
    status: 'anchored',
    lat: 6.4285,
    lng: 3.4219,
    speed: 0,
    heading: 180,
    course: 0,
    lastUpdate: '2025-01-10T08:00:00Z',
  },
  {
    id: 'v4',
    imo: '9521436',
    mmsi: '741852963',
    name: 'CMA CGM MARCO POLO',
    type: 'container',
    flag: 'France',
    destination: 'SHANGHAI',
    eta: '2025-01-20T22:00:00Z',
    status: 'underway',
    lat: 22.3193,
    lng: 114.1694,
    speed: 21.3,
    heading: 60,
    course: 58,
    lastUpdate: '2025-01-10T08:15:00Z',
  },
  {
    id: 'v5',
    imo: '9412587',
    mmsi: '369258147',
    name: 'ARCTIC VOYAGER',
    type: 'tanker',
    flag: 'Norway',
    destination: 'HOUSTON',
    eta: '2025-01-25T08:00:00Z',
    status: 'underway',
    lat: 29.7604,
    lng: -95.3698,
    speed: 14.8,
    heading: 270,
    course: 268,
    lastUpdate: '2025-01-10T08:20:00Z',
  },
]

// Bill of Lading Dummy Data
export const dummyBillsOfLading: BillOfLading[] = [
  {
    id: 'bol1',
    bolNumber: 'OTM-BOL-2025-001234',
    shipper: {
      name: 'Pacific Trade Co. Ltd',
      address: '123 Harbor Road, Hong Kong',
      email: 'exports@pacifictrade.hk',
      phone: '+852-2345-6789',
    },
    consignee: {
      name: 'Atlantic Imports LLC',
      address: '456 Dock Street, New York, NY 10001',
      email: 'imports@atlanticimports.com',
      phone: '+1-212-555-0123',
    },
    notifyParty: {
      name: 'Global Logistics Inc',
      address: '789 Freight Ave, Newark, NJ 07102',
      email: 'notify@globallogistics.com',
      phone: '+1-973-555-0456',
    },
    vesselName: 'MSC GENEVA',
    voyageNumber: 'VOY-2025-001',
    portOfLoading: 'HONG KONG',
    portOfDischarge: 'NEW YORK',
    placeOfDelivery: 'Newark, NJ',
    dateOfShipment: '2025-01-05',
    cargoDescription: 'Electronic Components - Circuit Boards and Semiconductors',
    grossWeight: 25000,
    weightUnit: 'kg',
    numberOfPackages: 500,
    packageType: 'Cartons',
    containerNumber: 'MSKU1234567',
    sealNumber: 'SL789012',
    freightCharges: 8500,
    freightPayableAt: 'origin',
    declaredValue: 250000,
    blockchainHash: '0x8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b',
    status: 'issued',
    validationStatus: 'valid',
    validationNotes: ['Blockchain verified', 'Weight matches manifest', 'Seal number confirmed'],
    createdAt: '2025-01-04T10:00:00Z',
    updatedAt: '2025-01-05T14:30:00Z',
    fee: 50,
  },
  {
    id: 'bol2',
    bolNumber: 'OTM-BOL-2025-001235',
    shipper: {
      name: 'Lagos Export Hub',
      address: '15 Marina Road, Lagos Island, Nigeria',
      email: 'shipping@lagosexport.ng',
      phone: '+234-1-234-5678',
    },
    consignee: {
      name: 'European Foods GmbH',
      address: 'Hafenstrasse 45, Hamburg 20457, Germany',
      email: 'import@eurofoods.de',
      phone: '+49-40-123-4567',
    },
    notifyParty: {
      name: 'European Foods GmbH',
      address: 'Hafenstrasse 45, Hamburg 20457, Germany',
      email: 'import@eurofoods.de',
      phone: '+49-40-123-4567',
    },
    vesselName: 'MAERSK EDINBURGH',
    voyageNumber: 'VOY-2025-042',
    portOfLoading: 'LAGOS',
    portOfDischarge: 'HAMBURG',
    placeOfDelivery: 'Hamburg, Germany',
    dateOfShipment: '2025-01-08',
    cargoDescription: 'Cocoa Beans - Premium Grade A',
    grossWeight: 50000,
    weightUnit: 'kg',
    numberOfPackages: 1000,
    packageType: 'Bags',
    containerNumber: 'MSKU7654321',
    sealNumber: 'SL456789',
    freightCharges: 12000,
    freightPayableAt: 'destination',
    declaredValue: 180000,
    blockchainHash: '0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b',
    status: 'verified',
    validationStatus: 'valid',
    validationNotes: ['Phytosanitary certificate attached', 'Origin verified'],
    createdAt: '2025-01-07T08:00:00Z',
    updatedAt: '2025-01-08T11:00:00Z',
    fee: 50,
  },
  {
    id: 'bol3',
    bolNumber: 'OTM-BOL-2025-001236',
    shipper: {
      name: 'Shanghai Manufacturing Co',
      address: '888 Industrial Park, Pudong, Shanghai',
      email: 'export@shanghaimfg.cn',
      phone: '+86-21-5555-8888',
    },
    consignee: {
      name: 'West Coast Distributors',
      address: '1200 Port Blvd, Long Beach, CA 90802',
      email: 'receiving@westcoastdist.com',
      phone: '+1-562-555-1200',
    },
    notifyParty: {
      name: 'Customs Broker Services',
      address: '500 Trade Center, Los Angeles, CA 90012',
      email: 'clearance@cbsbroker.com',
      phone: '+1-323-555-0500',
    },
    vesselName: 'CMA CGM MARCO POLO',
    voyageNumber: 'VOY-2025-088',
    portOfLoading: 'SHANGHAI',
    portOfDischarge: 'LONG BEACH',
    placeOfDelivery: 'Los Angeles, CA',
    dateOfShipment: '2025-01-02',
    cargoDescription: 'Furniture - Wooden Chairs and Tables',
    grossWeight: 35000,
    weightUnit: 'kg',
    numberOfPackages: 750,
    packageType: 'Crates',
    containerNumber: 'CMAU8765432',
    sealNumber: 'SL123456',
    freightCharges: 9800,
    freightPayableAt: 'origin',
    declaredValue: 145000,
    blockchainHash: '0x2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c',
    status: 'draft',
    validationStatus: 'pending',
    validationNotes: [],
    createdAt: '2025-01-01T15:00:00Z',
    updatedAt: '2025-01-02T09:00:00Z',
    fee: 50,
  },
]

// Shipping Quote Requests
export const dummyShippingQuotes: ShippingQuoteRequest[] = [
  {
    id: 'sq1',
    originPort: 'HONG KONG',
    destinationPort: 'LOS ANGELES',
    cargoType: 'Electronics',
    weight: 15000,
    volume: 45,
    dangerous: false,
    perishable: false,
    requestDate: '2025-01-08',
    status: 'quoted',
    quotes: [
      {
        id: 'q1',
        requestId: 'sq1',
        carrier: 'MSC',
        price: 4200,
        transitTime: 18,
        validUntil: '2025-01-20',
        notes: 'Direct service, weekly sailings',
      },
      {
        id: 'q2',
        requestId: 'sq1',
        carrier: 'Maersk',
        price: 4500,
        transitTime: 16,
        validUntil: '2025-01-20',
        notes: 'Express service available',
      },
    ],
  },
]

// Legal Consultants
export const dummyLegalConsultants: LegalConsultant[] = [
  {
    id: 'lc1',
    name: 'Dr. James Adewale',
    title: 'Senior Maritime Lawyer',
    specialization: ['Charterparty', 'Cargo Claims', 'Marine Insurance'],
    jurisdiction: ['Nigeria', 'UK', 'International'],
    rating: 4.9,
    reviewCount: 127,
    hourlyRate: 350,
    available: true,
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
    bio: 'Over 25 years of experience in maritime law with expertise in international shipping disputes.',
    experience: 25,
    email: 'j.adewale@maritimelaw.ng',
    phone: '+234-803-555-0001',
  },
  {
    id: 'lc2',
    name: 'Sarah Chen, LLM',
    title: 'Maritime Legal Consultant',
    specialization: ['Bill of Lading', 'NVOCC Regulations', 'Trade Compliance'],
    jurisdiction: ['Singapore', 'China', 'Hong Kong'],
    rating: 4.8,
    reviewCount: 89,
    hourlyRate: 400,
    available: true,
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150',
    bio: 'Specialized in Asian maritime trade law with focus on cross-border documentation.',
    experience: 15,
    email: 's.chen@maritimeconsult.sg',
    phone: '+65-9123-4567',
  },
  {
    id: 'lc3',
    name: 'Marcus Van Der Berg',
    title: 'Partner, Maritime Practice',
    specialization: ['Dispute Resolution', 'Arbitration', 'Ship Finance'],
    jurisdiction: ['Netherlands', 'EU', 'International'],
    rating: 4.7,
    reviewCount: 156,
    hourlyRate: 500,
    available: false,
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
    bio: 'Leading expert in maritime arbitration with extensive experience in London Maritime Arbitrators Association.',
    experience: 30,
    email: 'm.vanderberg@dutchlaw.nl',
    phone: '+31-20-555-0123',
  },
]

// Legal Templates
export const dummyLegalTemplates: LegalTemplate[] = [
  {
    id: 'lt1',
    name: 'Standard Charterparty Agreement',
    category: 'charterparty',
    description: 'BIMCO approved voyage charterparty template with comprehensive terms.',
    downloadUrl: '/templates/charterparty-standard.docx',
    premium: false,
    downloads: 1250,
  },
  {
    id: 'lt2',
    name: 'NVOCC Service Contract',
    category: 'nvocc',
    description: 'FMC compliant non-vessel operating common carrier service agreement.',
    downloadUrl: '/templates/nvocc-service-contract.docx',
    premium: true,
    downloads: 456,
  },
  {
    id: 'lt3',
    name: 'Bill of Lading Template',
    category: 'bol',
    description: 'Standard bill of lading form compliant with Hague-Visby Rules.',
    downloadUrl: '/templates/bol-template.docx',
    premium: false,
    downloads: 2340,
  },
  {
    id: 'lt4',
    name: 'Marine Insurance Claim Form',
    category: 'contract',
    description: 'Comprehensive claim form for cargo damage and loss claims.',
    downloadUrl: '/templates/marine-claim-form.docx',
    premium: false,
    downloads: 890,
  },
  {
    id: 'lt5',
    name: 'ISM Compliance Checklist',
    category: 'compliance',
    description: 'International Safety Management Code compliance audit checklist.',
    downloadUrl: '/templates/ism-checklist.xlsx',
    premium: true,
    downloads: 234,
  },
]

// Legal Services
export const dummyLegalServices: LegalService[] = [
  {
    id: 'ls1',
    type: 'contract_review',
    title: 'Charterparty Review',
    description: 'Expert review of charterparty agreements with detailed risk assessment.',
    price: 750,
    duration: '2-3 business days',
  },
  {
    id: 'ls2',
    type: 'compliance_consulting',
    title: 'IMO Compliance Audit',
    description: 'Full compliance audit against IMO regulations and recommendations.',
    price: 2500,
    duration: '5-7 business days',
  },
  {
    id: 'ls3',
    type: 'advisory',
    title: 'Multi-Jurisdiction Advisory',
    description: 'Legal guidance for operations spanning multiple maritime jurisdictions.',
    price: 1500,
    duration: 'Ongoing',
  },
  {
    id: 'ls4',
    type: 'dispute_resolution',
    title: 'Pre-Arbitration Assessment',
    description: 'Evaluation of dispute merits and recommended resolution strategy.',
    price: 1000,
    duration: '3-5 business days',
  },
]

// Legal Resources
export const dummyLegalResources: LegalResource[] = [
  {
    id: 'lr1',
    title: 'Understanding Maritime Liens',
    type: 'article',
    category: 'Ship Finance',
    summary: 'Comprehensive guide to maritime liens and their enforcement across jurisdictions.',
    url: '/resources/maritime-liens-guide.pdf',
    premium: false,
    publishDate: '2024-12-15',
  },
  {
    id: 'lr2',
    title: 'SOLAS Compliance Checklist 2025',
    type: 'checklist',
    category: 'Safety Compliance',
    summary: 'Updated checklist for Safety of Life at Sea convention requirements.',
    url: '/resources/solas-checklist-2025.pdf',
    premium: false,
    publishDate: '2025-01-01',
  },
  {
    id: 'lr3',
    title: 'Maritime Cyber Security Framework',
    type: 'framework',
    category: 'Cyber Security',
    summary: 'IMO recommended framework for vessel cyber risk management.',
    url: '/resources/cyber-security-framework.pdf',
    premium: true,
    publishDate: '2024-11-20',
  },
]

// Security Contacts
export const dummySecurityContacts: SecurityContact[] = [
  {
    agency: 'Nigerian Maritime Police',
    name: 'Maritime Command Center',
    phone: '+234-1-790-1234',
    email: 'emergency@maritimepolice.ng',
    region: 'Nigeria',
    available24x7: true,
  },
  {
    agency: 'EFCC Maritime Fraud Unit',
    name: 'Economic Crime Division',
    phone: '+234-1-844-5555',
    email: 'maritime.fraud@efcc.gov.ng',
    region: 'Nigeria',
    available24x7: false,
  },
  {
    agency: 'Nigerian Navy Coast Guard',
    name: 'Western Naval Command',
    phone: '+234-1-263-0001',
    email: 'coastguard@navy.mil.ng',
    region: 'Nigeria - Western Region',
    available24x7: true,
  },
  {
    agency: 'NIMASA Security',
    name: 'Maritime Security Center',
    phone: '+234-1-460-2000',
    email: 'security@nimasa.gov.ng',
    region: 'Nigeria - National',
    available24x7: true,
  },
]

// Security Reports
export const dummySecurityReports: SecurityReport[] = [
  {
    id: 'sr1',
    type: 'fraud',
    urgency: 'high',
    agency: 'efcc',
    title: 'Suspected Fraudulent Bill of Lading',
    description: 'Received a bill of lading that appears to have been altered. Container seal numbers do not match shipping records.',
    location: 'Lagos Port Complex',
    coordinates: { lat: 6.4285, lng: 3.4219 },
    evidence: ['bol-scan.pdf', 'original-manifest.pdf'],
    reporterContact: {
      name: 'John Okafor',
      email: 'j.okafor@importco.ng',
      phone: '+234-803-555-1234',
      anonymous: false,
    },
    status: 'investigating',
    caseNumber: 'EFCC-MAR-2025-0042',
    createdAt: '2025-01-08T14:30:00Z',
    updatedAt: '2025-01-09T09:00:00Z',
  },
]

// Dispute Cases
export const dummyDisputeCases: DisputeCase[] = [
  {
    id: 'dc1',
    caseNumber: 'ARB-2025-00123',
    type: 'cargo_damage',
    complainantId: 'user1',
    complainantName: 'Atlantic Imports LLC',
    respondentId: 'user2',
    respondentName: 'Pacific Trade Co. Ltd',
    transactionId: 'tx1',
    bolNumber: 'OTM-BOL-2025-001234',
    description: 'Electronic components received with water damage. Approximately 30% of shipment affected. Seeking compensation for damaged goods valued at $75,000.',
    claimAmount: 75000,
    evidence: [
      { id: 'e1', name: 'damage-photos.zip', type: 'image', url: '/evidence/damage-photos.zip' },
      { id: 'e2', name: 'survey-report.pdf', type: 'document', url: '/evidence/survey-report.pdf' },
      { id: 'e3', name: 'original-bol.pdf', type: 'document', url: '/evidence/original-bol.pdf' },
    ],
    status: 'ai_analysis',
    aiAnalysis: {
      breachEvaluation: 'Based on the evidence provided, there appears to be a clear breach of the carrier\'s duty to deliver goods in the same condition as shipped. The survey report confirms water ingress occurred during transit.',
      liabilityScore: 78,
      riskScore: 65,
      settlementRecommendation: 'Recommend settlement at 80% of claimed amount ($60,000) based on comparative liability analysis. Carrier likely liable under Hague-Visby Rules Article III.',
      deviationReport: 'Vessel deviated from standard route during transit. Weather data indicates encounter with tropical storm in South China Sea on January 3rd.',
      confidenceLevel: 85,
      analyzedAt: '2025-01-09T15:30:00Z',
    },
    createdAt: '2025-01-09T10:00:00Z',
    updatedAt: '2025-01-09T15:30:00Z',
  },
  {
    id: 'dc2',
    caseNumber: 'ARB-2025-00124',
    type: 'shipment_delay',
    complainantId: 'user3',
    complainantName: 'European Foods GmbH',
    respondentId: 'user4',
    respondentName: 'Lagos Export Hub',
    transactionId: 'tx2',
    bolNumber: 'OTM-BOL-2025-001235',
    description: 'Cocoa shipment arrived 15 days late causing contract penalties with end buyer. Claiming damages for delay-related losses.',
    claimAmount: 25000,
    evidence: [
      { id: 'e4', name: 'contract-terms.pdf', type: 'document', url: '/evidence/contract-terms.pdf' },
      { id: 'e5', name: 'delay-correspondence.pdf', type: 'document', url: '/evidence/delay-correspondence.pdf' },
    ],
    status: 'under_review',
    aiAnalysis: {
      breachEvaluation: 'Delay of 15 days exceeds reasonable tolerance. However, evidence suggests port congestion was a contributing factor which may limit carrier liability.',
      liabilityScore: 55,
      riskScore: 40,
      settlementRecommendation: 'Recommend settlement at 50% of claimed amount ($12,500). Delay partially attributable to force majeure conditions at loading port.',
      deviationReport: 'No route deviation detected. Delay primarily occurred at port of loading due to documented congestion.',
      confidenceLevel: 72,
      analyzedAt: '2025-01-08T11:00:00Z',
    },
    createdAt: '2025-01-07T09:00:00Z',
    updatedAt: '2025-01-09T14:00:00Z',
  },
]

// Marketplace Listings
export const dummyMarketplaceListings: MarketplaceListing[] = [
  {
    id: 'ml1',
    sellerId: 'seller1',
    sellerName: 'Global Container Solutions',
    sellerRating: 4.8,
    category: 'container',
    title: '40ft High Cube Container - Excellent Condition',
    description: 'One-trip 40ft high cube container suitable for cargo or conversion. CSC certified, watertight, and clean interior.',
    price: 3500,
    priceType: 'fixed',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600',
      'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=600',
    ],
    location: {
      country: 'Nigeria',
      city: 'Lagos',
      port: 'Apapa Port',
      coordinates: { lat: 6.4285, lng: 3.4219 },
    },
    specifications: {
      'External Length': '40ft (12.19m)',
      'External Width': '8ft (2.44m)',
      'External Height': '9ft 6in (2.90m)',
      'Tare Weight': '3,940 kg',
      'Max Payload': '28,560 kg',
      'Cubic Capacity': '76.4 m³',
    },
    condition: 'like_new',
    availability: 'available',
    bolRequired: false,
    bolVerified: false,
    featured: true,
    views: 234,
    inquiries: 12,
    createdAt: '2025-01-05T10:00:00Z',
    updatedAt: '2025-01-09T16:00:00Z',
  },
  {
    id: 'ml2',
    sellerId: 'seller2',
    sellerName: 'Maritime Equipment Nigeria',
    sellerRating: 4.6,
    category: 'crane',
    title: 'Liebherr LHM 420 Mobile Harbor Crane',
    description: 'Well-maintained mobile harbor crane with 124t maximum capacity. Perfect for container and general cargo handling.',
    price: 850000,
    priceType: 'negotiable',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=600',
    ],
    location: {
      country: 'Nigeria',
      city: 'Port Harcourt',
      port: 'Onne Port',
    },
    specifications: {
      'Max Lifting Capacity': '124 tonnes',
      'Max Outreach': '48 meters',
      'Engine': 'Liebherr Diesel',
      'Year': '2019',
      'Operating Hours': '8,500',
    },
    condition: 'good',
    availability: 'available',
    bolRequired: true,
    bolNumber: 'OTM-BOL-2024-045678',
    bolVerified: true,
    featured: true,
    views: 89,
    inquiries: 5,
    createdAt: '2025-01-02T08:00:00Z',
    updatedAt: '2025-01-08T12:00:00Z',
  },
  {
    id: 'ml3',
    sellerId: 'seller3',
    sellerName: 'Lagos Warehouse Co',
    sellerRating: 4.9,
    category: 'warehouse',
    title: 'Bonded Warehouse Space - 5000 sqm',
    description: 'Customs bonded warehouse facility with 24/7 security, climate control options, and direct port access.',
    price: 15000,
    priceType: 'per_month',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1553413077-190dd305871c?w=600',
    ],
    location: {
      country: 'Nigeria',
      city: 'Lagos',
      port: 'Tin Can Island Port',
    },
    specifications: {
      'Floor Space': '5,000 sqm',
      'Ceiling Height': '10 meters',
      'Loading Bays': '8',
      'Security': '24/7 CCTV + Guards',
      'Climate Control': 'Available',
      'Customs Bond': 'Active',
    },
    condition: 'good',
    availability: 'available',
    bolRequired: false,
    bolVerified: false,
    featured: false,
    views: 156,
    inquiries: 23,
    createdAt: '2024-12-20T14:00:00Z',
    updatedAt: '2025-01-07T09:00:00Z',
  },
  {
    id: 'ml4',
    sellerId: 'seller4',
    sellerName: 'Marine Parts International',
    sellerRating: 4.5,
    category: 'spare_parts',
    title: 'MAN B&W Marine Engine Spare Parts Kit',
    description: 'Complete maintenance kit for MAN B&W 6S50MC-C marine engine. Includes pistons, rings, bearings, and gaskets.',
    price: 45000,
    priceType: 'fixed',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=600',
    ],
    location: {
      country: 'Singapore',
      city: 'Singapore',
      port: 'Jurong Port',
    },
    specifications: {
      'Engine Model': 'MAN B&W 6S50MC-C',
      'Kit Contents': 'Pistons, Rings, Bearings, Gaskets',
      'OEM': 'Yes',
      'Warranty': '12 months',
    },
    condition: 'new',
    availability: 'available',
    bolRequired: true,
    bolVerified: false,
    featured: false,
    views: 67,
    inquiries: 8,
    createdAt: '2025-01-03T11:00:00Z',
    updatedAt: '2025-01-09T08:00:00Z',
  },
  {
    id: 'ml5',
    sellerId: 'seller5',
    sellerName: 'African Maritime Insurance',
    sellerRating: 4.7,
    category: 'insurance',
    title: 'Comprehensive Marine Cargo Insurance',
    description: 'All-risk cargo insurance covering door-to-door shipments. Competitive rates with quick claim settlement.',
    price: 0,
    priceType: 'negotiable',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=600',
    ],
    location: {
      country: 'Nigeria',
      city: 'Lagos',
    },
    specifications: {
      'Coverage': 'All Risk',
      'Territory': 'Worldwide',
      'Claim Settlement': '30 days',
      'Minimum Premium': '$500',
    },
    condition: 'new',
    availability: 'available',
    bolRequired: false,
    bolVerified: false,
    featured: true,
    views: 312,
    inquiries: 45,
    createdAt: '2024-12-15T09:00:00Z',
    updatedAt: '2025-01-09T10:00:00Z',
  },
  {
    id: 'ml6',
    sellerId: 'seller6',
    sellerName: 'Port Repair Services',
    sellerRating: 4.4,
    category: 'repairs',
    title: 'Container Repair & Refurbishment Services',
    description: 'Professional container repair services including welding, painting, floor replacement, and CSC recertification.',
    price: 500,
    priceType: 'negotiable',
    currency: 'USD',
    images: [
      'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600',
    ],
    location: {
      country: 'Nigeria',
      city: 'Lagos',
      port: 'Apapa Port',
    },
    specifications: {
      'Services': 'Welding, Painting, Flooring',
      'Turnaround': '3-5 days',
      'Certification': 'CSC Re-certification',
      'Warranty': '6 months',
    },
    condition: 'new',
    availability: 'available',
    bolRequired: false,
    bolVerified: false,
    featured: false,
    views: 89,
    inquiries: 15,
    createdAt: '2025-01-01T08:00:00Z',
    updatedAt: '2025-01-08T14:00:00Z',
  },
]

// Seller Profiles
export const dummySellerProfiles: SellerProfile[] = [
  {
    id: 'seller1',
    businessName: 'Global Container Solutions',
    email: 'sales@globalcontainers.ng',
    phone: '+234-803-555-0001',
    country: 'Nigeria',
    verified: true,
    rating: 4.8,
    totalSales: 156,
    memberSince: '2022-03-15',
    listings: 24,
    responseRate: 98,
    responseTime: 'Within 2 hours',
  },
  {
    id: 'seller2',
    businessName: 'Maritime Equipment Nigeria',
    email: 'info@maritimeequip.ng',
    phone: '+234-803-555-0002',
    country: 'Nigeria',
    verified: true,
    rating: 4.6,
    totalSales: 45,
    memberSince: '2023-01-20',
    listings: 12,
    responseRate: 95,
    responseTime: 'Within 4 hours',
  },
]

// Purchase Requests
export const dummyPurchaseRequests: PurchaseRequest[] = [
  {
    id: 'pr1',
    listingId: 'ml1',
    buyerId: 'buyer1',
    buyerName: 'West African Shipping Co',
    sellerId: 'seller1',
    sellerName: 'Global Container Solutions',
    message: 'Interested in purchasing 5 units. Can you offer bulk pricing?',
    verificationStatus: 'verified',
    aiValidation: {
      bolValid: true,
      ownershipVerified: true,
      originVerified: true,
      destinationVerified: true,
      inconsistencies: [],
      riskLevel: 'low',
      validatedAt: '2025-01-09T14:00:00Z',
    },
    status: 'pending',
    paymentStatus: 'unpaid',
    createdAt: '2025-01-09T13:00:00Z',
    updatedAt: '2025-01-09T14:00:00Z',
  },
]

// Knowledge Resources
export const dummyKnowledgeResources: KnowledgeResource[] = [
  {
    id: 'kr1',
    title: 'IMO Guidelines for Maritime Operations',
    type: 'pdf',
    category: 'imo',
    description: 'Official IMO guidelines covering safety, environmental, and operational standards.',
    url: '/knowledge/imo-guidelines-2025.pdf',
    fileSize: '4.5 MB',
    pages: 156,
    createdAt: '2025-01-01',
    updatedAt: '2025-01-01',
  },
  {
    id: 'kr2',
    title: 'Customs Clearance Procedures Manual',
    type: 'guide',
    category: 'customs',
    description: 'Step-by-step guide for customs clearance in major Nigerian ports.',
    url: '/knowledge/customs-manual.pdf',
    fileSize: '2.8 MB',
    pages: 84,
    createdAt: '2024-12-15',
    updatedAt: '2025-01-05',
  },
  {
    id: 'kr3',
    title: 'AIS Data Standards & Protocols',
    type: 'training',
    category: 'ais',
    description: 'Technical documentation on Automatic Identification System standards.',
    url: '/knowledge/ais-standards.pdf',
    fileSize: '1.2 MB',
    pages: 45,
    createdAt: '2024-11-20',
    updatedAt: '2024-11-20',
  },
  {
    id: 'kr4',
    title: 'Maritime Safety Training Module',
    type: 'training',
    category: 'safety',
    description: 'Comprehensive safety training material for maritime personnel.',
    url: '/knowledge/safety-training.pdf',
    fileSize: '8.5 MB',
    pages: 220,
    createdAt: '2024-10-01',
    updatedAt: '2024-12-20',
  },
  {
    id: 'kr5',
    title: 'IMO Official Website',
    type: 'external_link',
    category: 'imo',
    description: 'International Maritime Organization official resources and updates.',
    url: 'https://www.imo.org',
    externalSource: 'IMO',
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  {
    id: 'kr6',
    title: 'Nigeria Customs Service Portal',
    type: 'external_link',
    category: 'customs',
    description: 'Official Nigeria Customs Service for tariffs and regulations.',
    url: 'https://www.customs.gov.ng',
    externalSource: 'NCS',
    createdAt: '2024-01-01',
    updatedAt: '2024-01-01',
  },
  {
    id: 'kr7',
    title: 'ISPS Code Compliance Guide',
    type: 'guide',
    category: 'compliance',
    description: 'International Ship and Port Facility Security Code implementation guide.',
    url: '/knowledge/isps-compliance.pdf',
    fileSize: '3.2 MB',
    pages: 98,
    createdAt: '2024-09-15',
    updatedAt: '2024-12-01',
  },
  {
    id: 'kr8',
    title: 'Port Operations Best Practices',
    type: 'policy',
    category: 'operations',
    description: 'Industry best practices for efficient port operations and cargo handling.',
    url: '/knowledge/port-operations.pdf',
    fileSize: '2.1 MB',
    pages: 67,
    createdAt: '2024-08-10',
    updatedAt: '2024-11-15',
  },
]
